import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";

const DEFAULT_PHASES = [
    { id: "ABSTRACT", label: "Abstracts Screening Phase", shortLabel: "Abstracts Screening" },
    { id: "FULL_TEXT", label: "Full-Text Screening (Only Available PDF's)", shortLabel: "Full-Text Screening" },
];

const FabricGroupCard = ({ group, selectedProject, isFabricActive, onSelectFabric, onSelectThread }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="mb-1">
            <div
                className={`w-100 d-flex align-items-center justify-content-between px-3 py-2 ev-radius-small cursor-pointer border-0 mb-1 ${
                    isFabricActive
                        ? "ev-btn-dark-blue-fill-round"
                        : "ev-btn-white-fill-round"
                }`}
                onClick={onSelectFabric}
            >
                <div className="d-flex align-items-center gap-2 overflow-hidden">
                    <i className={`bi bi-folder2 ${isFabricActive ? "text-white" : "ev-dark-blue"}`} />
                    <span className={`ev-bold-med text-truncate ${isFabricActive ? "text-white" : "ev-dark-blue"}`}>
                        {group.fabric_name}
                    </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {isFabricActive && <i className="bi bi-check-circle-fill text-white" />}
                    {(group.threads?.length > 0) && (
                        <i
                            className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} ${isFabricActive ? "text-white" : "ev-dark-blue"}`}
                            onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
                        />
                    )}
                </div>
            </div>

            {isOpen && (group.threads || []).map((thread) => {
                const isThreadActive = selectedProject?.thread_id === thread.id;
                return (
                    <div
                        key={thread.id}
                        className={`w-100 d-flex align-items-center justify-content-between px-3 py-2 ev-radius-small cursor-pointer border-0 mb-1 ms-2 ${
                            isThreadActive
                                ? "ev-btn-dark-blue-fill-round"
                                : "ev-btn-white-fill-round"
                        }`}
                        style={{ width: "calc(100% - 8px)" }}
                        onClick={() => onSelectThread(thread)}
                    >
                        <div className="d-flex align-items-center gap-2 overflow-hidden">
                            <i className={`bi bi-journal-text ${isThreadActive ? "text-white" : "ev-dark-blue"}`} />
                            <span className={`ev-bold-med text-truncate ${isThreadActive ? "text-white" : "ev-dark-blue"}`}>
                                {thread.name}
                            </span>
                        </div>
                        {isThreadActive && <i className="bi bi-check-circle-fill text-white" />}
                    </div>
                );
            })}
        </div>
    );
};

const MigrateDropdown = ({
    onMigrate,
    disabled = false,
    isLoading = false,

    selectedProject,
    onSelectProject,

    phases = DEFAULT_PHASES,
    selectedPhase,
    onSelectPhase,

    abstractMigrationStatus = null,
    fulltextMigrationStatus = null,
    showPhase = true,
    dropdownAlign = "start",
    fdaMode = false,
}) => {
    const navigate = useNavigate();
    const ref = useRef(null);
    const searchTimeoutRef = useRef(null);

    const API_BASE_URL = '/pceapi/slr';

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    const [openProject, setOpenProject] = useState(false);
    const [openPhase, setOpenPhase] = useState(false);
    const [projectType, setProjectType] = useState(null);
    const [collectionMode, setCollectionMode] = useState("projects"); // "projects" | "collections"

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [size] = useState(25);

    const totalPages = Math.max(1, Math.ceil(total / size));

    const fetchProjects = useCallback(async (pageNum, searchTerm, type, mode) => {
        const resolvedMode = mode ?? collectionMode;
        setLoading(true);
        try {
            if ((type || projectType) === "SLR") {
                const res = await axios.post(
                    `${API_BASE_URL}/list`,
                    { page: pageNum, size: size, search: searchTerm || null },
                    { headers }
                );
                if (res.data?.status === "NotAuthorized") {
                    authNavigate('/login', 'iam', navigate);
                    return;
                }
                if (res.data?.status === "success") {
                    setProjects(res.data.data || []);
                    setTotal(res.data.total || 0);
                }
            } else {
                const res = await axios.post(
                    `/pceapi/slr/discovery/data-collections`,
                    {
                        page: pageNum,
                        size: size,
                        search: searchTerm || null,
                        collection_type: resolvedMode === "collections" ? "standalone" : "project",
                    },
                    { headers }
                );
                if (res.data?.status === "NotAuthorized") {
                    authNavigate('/login', 'iam', navigate);
                    return;
                }
                if (res.data?.status === "success") {
                    const d = res.data.data || {};
                    if (resolvedMode === "collections") {
                        setProjects(d.standalone_collections || []);
                    } else {
                        const grouped = (d.project_collections || []).map((pc) => ({
                            type: "group",
                            fabric_id: pc.fabric_id,
                            fabric_name: pc.fabric_name,
                            threads: pc.threads || [],
                        }));
                        setProjects(grouped);
                    }
                    setTotal(res.data.total || 0);
                }
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
            console.error("Failed to fetch projects", err);
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
            }
        }
        }, [headers, size, navigate, projectType, collectionMode]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setPage(1);
            fetchProjects(1, val);
        }, 500);
    };

    const handleProjectTypeChange = (type) => {
        setProjectType(type);
        setCollectionMode("projects");
        setPage(1);
        setSearch("");
        setProjects([]);
        onSelectProject?.(null);
        fetchProjects(1, "", type, "projects");
    };

    const toggleProject = () => {
        setOpenProject((prev) => !prev);
        setOpenPhase(false);
    };

    const togglePhase = () => {
        setOpenPhase((prev) => !prev);
        setOpenProject(false);
    };

    const handleSelectProject = (project) => {
        onSelectProject?.(project);
        setOpenProject(false);
    };

    const handleSelectPhase = (phase) => {
        onSelectPhase?.(phase);
        setOpenPhase(false);
    };

    const getMigrationStatus = (phaseId) => {
        if (phaseId === "ABSTRACT") return abstractMigrationStatus;
        if (phaseId === "FULL_TEXT") return fulltextMigrationStatus;
        return null;
    };

    const renderStatusBadge = (status) => {
        if (!status) {
            return (
                <span className="category-badge ev-btn-xsmall">
                    Not Migrated
                </span>
            );
        }
        if (status === "RUNNING") {
            return (
                <span className="category-badge category-badge-3 ev-btn-xsmall">
                    Running
                </span>
            );
        }
        if (status === "COMPLETED") {
            return (
                <span className="category-badge category-badge-1 ev-btn-xsmall">
                    Completed
                </span>
            );
        }
        if (status === "FAILED") {
            return (
                <span className="category-badge category-badge-2 ev-btn-xsmall">
                    Failed
                </span>
            );
        }
        return null;
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpenProject(false);
                setOpenPhase(false);
                setOpenDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Sirf page change pe refetch
    useEffect(() => {
        if (openProject && projectType !== null) {
            fetchProjects(page, search, projectType, collectionMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const [openDropdown, setOpenDropdown] = useState(false);

    const toggleDropdown = () => {
        setOpenDropdown((prev) => !prev);
        setOpenProject(false);
        setOpenPhase(false);
    };

    return (
        <div
            ref={ref}
            className="position-relative d-inline-flex align-items-center"
        >
            {/* Single toggle button */}
            <div className="d-flex align-items-center btn-group custom-btn-group-outer">
                {abstractMigrationStatus === "RUNNING" && (
                    <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                        Running
                    </span>
                )}
                {abstractMigrationStatus === "COMPLETED" && (
                    <span className="btn genbi-custom-btn-group-inner ev-btn-blue-fill-round ev-btn-small ev-radius-large">
                        Completed
                    </span>
                )}
                {abstractMigrationStatus === "FAILED" && (
                    <span className="btn genbi-custom-btn-group-inner ev-btn-red-fill-round ev-btn-small ev-radius-large">
                        Failed
                    </span>
                )}
                <button
                    type="button"
                    className="btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round ev-btn-small ev-bold-med d-flex align-items-center gap-2"
                    onClick={toggleDropdown}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Migrating...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-left-right" />
                            Migrate
                            <i className={`bi ${openDropdown && !openProject ? "bi-chevron-up" : "bi-chevron-down"}`} />
                        </>
                    )}
                </button>
            </div>

            {/* Combined dropdown panel */}
            {openDropdown && (
                <div
                    className="ev-card-8 ev-radius-xsmall position-absolute top-100 end-0 mt-2 p-3 shadow"
                    style={{ zIndex: 1060, minWidth: "320px" }}
                >
                    {/* Project row */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="ev-small-p-dark ev-bold-med">Project</span>
                        <button
                            type="button"
                            className="ev-btn-white-fill-round ev-btn-small d-flex align-items-center gap-2 ev-btn-border-square ev-med-p-blue"
                            onClick={toggleProject}
                        >
                            <span className="d-inline-block text-truncate" style={{ maxWidth: "140px" }}>
                                {selectedProject?.name || "Select Project"}
                            </span>
                            <i className={`bi ${openProject ? "bi-chevron-up" : "bi-chevron-down"}`} />
                        </button>
                    </div>

                    {/* Inline project picker — shown below Select Project row */}
                    {openProject && (
                        <div className={`mb-2 ${projectType === null ? "" : "ev-card-3 p-2 ev-radius-small"}`}>
                            {projectType === null ? (
                                <>
                                    <button
                                        type="button"
                                        className="w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ev-btn-light-fill ev-reg-p-blue"
                                        onClick={() => handleProjectTypeChange("SLR")}
                                    >
                                        SLR Projects
                                    </button>
                                    <button
                                        type="button"
                                        className="w-100 text-start ev-radius-xsmall ev-btn-small ev-btn-light-fill ev-reg-p-blue"
                                        onClick={() => handleProjectTypeChange("ALL")}
                                    >
                                        Data Collections
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                type="button"
                                                className="ev-btn-light-fill ev-btn-xsmall"
                                                onClick={() => {
                                                    setProjectType(null);
                                                    setProjects([]);
                                                    setSearch("");
                                                    setPage(1);
                                                    setCollectionMode("projects");
                                                }}
                                            >
                                                <i className="bi bi-chevron-left" />
                                            </button>
                                            <span className="ev-small-p-dark ev-bold-med">
                                                {projectType === "SLR" ? "SLR Projects" : "All Projects"}
                                            </span>
                                        </div>

                                        {projectType === "ALL" && (
                                            <div className="ev-message-card6 ev-radius-small p-1 d-flex gap-1">
                                                <button
                                                    type="button"
                                                    className={collectionMode === "projects"
                                                        ? "ev-btn-blue-fill-round ev-btn-xsmall ev-bold-med"
                                                        : "ev-btn-light-fill ev-btn-small ev-bold-med"}
                                                    onClick={() => {
                                                        if (collectionMode !== "projects") {
                                                            setLoading(true);
                                                            setProjects([]);
                                                            setCollectionMode("projects");
                                                            setPage(1);
                                                            setSearch("");
                                                            onSelectProject?.(null);
                                                            fetchProjects(1, "", "ALL", "projects");
                                                        }
                                                    }}
                                                >
                                                    Projects
                                                </button>
                                                <button
                                                    type="button"
                                                    className={collectionMode === "collections"
                                                        ? "ev-btn-blue-fill-round ev-btn-xsmall ev-bold-med"
                                                        : "ev-btn-light-fill ev-btn-xsmall ev-bold-med"}
                                                    onClick={() => {
                                                        if (collectionMode !== "collections") {
                                                            setLoading(true);
                                                            setProjects([]);
                                                            setCollectionMode("collections");
                                                            setPage(1);
                                                            setSearch("");
                                                            onSelectProject?.(null);
                                                            fetchProjects(1, "", "ALL", "collections");
                                                        }
                                                    }}
                                                >
                                                    Collections
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <div className="position-relative w-100">
                                            <input
                                                type="text"
                                                className="form-control search-input ms-0"
                                                placeholder="Search projects"
                                                value={search}
                                                onChange={handleSearchChange}
                                                autoFocus
                                            />
                                            <button
                                                className="ev-btn-lightblue-fill-round search-bar-button"
                                                onClick={() => fetchProjects(1, search)}
                                                type="button"
                                            >
                                                <i className="bi bi-search"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="scrollable-element vh-30 pe-2">
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary spinner-border-sm mb-2"></div>
                                                <p className="ev-xsmall-p-dark">Loading projects...</p>
                                            </div>
                                        ) : projects.length === 0 ? (
                                            <div className="text-center py-5">
                                                <p className="ev-reg-p-dark text-muted">No projects found.</p>
                                            </div>
                                        ) : (
                                            <div className="d-flex flex-column gap-2">
                                                {projectType === "ALL" && collectionMode === "collections" ? (
                                                    projects.map((p) => {
                                                        const isActive = selectedProject?.id === p.id;
                                                        return (
                                                            <div
                                                                key={p.id}
                                                                className={`w-100 d-flex align-items-center justify-content-between px-3 py-2 ev-radius-small cursor-pointer border-0 mb-1 ${isActive ? "ev-btn-dark-blue-fill-round" : "ev-btn-white-fill-round"}`}
                                                                onClick={() => handleSelectProject({ id: p.id, name: p.name, fabric_id: null, thread_id: p.id })}
                                                            >
                                                                <span className={`ev-bold-med text-truncate ${isActive ? "text-white" : "ev-dark-blue"}`}>{p.name}</span>
                                                                {isActive && <i className="bi bi-check-circle-fill text-white"></i>}
                                                            </div>
                                                        );
                                                    })
                                                ) : projectType === "ALL" ? (
                                                    projects.map((group) => {
                                                        const isFabricActive = selectedProject?.fabric_id === group.fabric_id && !selectedProject?.thread_id;
                                                        return (
                                                            <FabricGroupCard
                                                                key={group.fabric_id}
                                                                group={group}
                                                                selectedProject={selectedProject}
                                                                isFabricActive={isFabricActive}
                                                                onSelectFabric={() => handleSelectProject({ id: group.fabric_id, name: group.fabric_name, fabric_id: group.fabric_id, thread_id: null })}
                                                                onSelectThread={(thread) => handleSelectProject({ id: thread.id, name: thread.name, fabric_id: group.fabric_id, thread_id: thread.id })}
                                                            />
                                                        );
                                                    })
                                                ) : (
                                                    projects.map((p) => {
                                                        const isActive = selectedProject?.id === p.id;
                                                        return (
                                                            <div
                                                                key={p.id}
                                                                className={`w-100 d-flex align-items-center justify-content-between px-3 py-2 ev-radius-small cursor-pointer border-0 ${isActive ? "ev-btn-dark-blue-fill-round" : "ev-btn-white-fill-round"}`}
                                                                onClick={() => handleSelectProject({ id: p.id, name: p.name, fabric_id: p.id, thread_id: null })}
                                                            >
                                                                <span className={`ev-bold-med text-truncate ${isActive ? "text-white" : "ev-dark-blue"}`}>{p.name}</span>
                                                                {isActive && <i className="bi bi-check-circle-fill text-white"></i>}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                                        <button
                                            className="ev-btn-light-fill ev-btn-small"
                                            disabled={page === 1 || loading}
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            type="button"
                                        >
                                            <i className="bi bi-chevron-left"></i>
                                        </button>
                                        <button className="ev-btn-light-fill ev-btn-small px-3" disabled type="button">
                                            Page {page} of {totalPages}
                                        </button>
                                        <button
                                            className="ev-btn-light-fill ev-btn-small"
                                            disabled={page >= totalPages || loading}
                                            onClick={() => setPage(p => p + 1)}
                                            type="button"
                                        >
                                            <i className="bi bi-chevron-right"></i>
                                        </button>
                                        <span className="ev-btn-light-fill">{size}/page</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Phase row — static, always Abstracts Screening */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="ev-small-p-dark ev-bold-med">Phase</span>
                        <span className="ev-btn-white-fill-round ev-btn-small d-flex align-items-center gap-2 ev-btn-border-square ev-med-p-blue">
                            Abstracts Screening
                        </span>
                    </div>

                    {/* Migrate action */}
                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="ev-btn-blue-fill-round ev-btn-small d-flex align-items-center gap-2"
                            onClick={() => {
                                onMigrate?.({ project: selectedProject, phase: selectedPhase });
                                setOpenDropdown(false);
                            }}
                            disabled={disabled || isLoading || !selectedProject}
                        >
                            <i className="bi bi-arrow-left-right" />
                            Migrate
                        </button>
                    </div>
                </div>
            )}


        </div>
    );
};

export default MigrateDropdown;